/**
 * SAB-988 part 1: the in-room draft cue.
 *
 * ── SYNTHESISED, NOT AN AUDIO FILE ──────────────────────────────────────────
 *
 * Two tones from the Web Audio API rather than a bundled mp3. No asset to ship, no request
 * that can 404 on a flaky draft-night connection, no cache-busting, and it works offline.
 * The whole thing is a few oscillator nodes; an mp3 would be larger than this file.
 *
 * ── THE BROWSER WILL NOT LET YOU PLAY SOUND UNPROMPTED ──────────────────────
 *
 * An AudioContext created without a user gesture starts SUSPENDED, and every call to play
 * is silently dropped -- no error, no sound. That is the failure mode this module is shaped
 * around: `unlock()` must be called from a real gesture (a click/tap anywhere in the room),
 * and `isReady()` reports whether the context is actually running so the UI can tell the
 * user the cue is armed instead of leaving them to discover it did not fire.
 *
 * We do NOT create the context at import time. Some browsers count that against the
 * autoplay policy, and it would also spin up audio hardware for users who never draft.
 */

let ctx: AudioContext | null = null;

type Ctor = typeof AudioContext;
function audioCtor(): Ctor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/** True when a cue would actually be audible right now. */
export function isReady(): boolean {
  return !!ctx && ctx.state === 'running';
}

/**
 * Create/resume the context. Safe to call repeatedly; must originate from a user gesture
 * the first time or the context stays suspended.
 */
export async function unlock(): Promise<boolean> {
  const Ctor = audioCtor();
  if (!Ctor) return false;
  try {
    if (!ctx) ctx = new Ctor();
    if (ctx.state === 'suspended') await ctx.resume();
    return ctx.state === 'running';
  } catch {
    // A browser with audio disabled entirely. The room must not break over a chime.
    return false;
  }
}

/** One shaped tone. Gain ramps rather than switches, or the speaker pops. */
function tone(startAt: number, freq: number, durationMs: number, peak: number): void {
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, startAt);
  const dur = durationMs / 1000;
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + dur);
  osc.connect(gain).connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + dur + 0.02);
}

/**
 * "You are on the clock" — a rising two-note figure.
 *
 * Rising because it is an invitation to act. The warning below falls, so the two are
 * distinguishable without looking at the screen, which is the entire point of a sound cue.
 */
export function playOnClock(): void {
  if (!isReady() || !ctx) return;
  const t = ctx.currentTime;
  tone(t, 660, 160, 0.18);
  tone(t + 0.17, 880, 260, 0.18);
}

/** "30 seconds left" — a lower, falling double-beep. Deliberately less pleasant. */
export function playWarning(): void {
  if (!isReady() || !ctx) return;
  const t = ctx.currentTime;
  tone(t, 520, 130, 0.15);
  tone(t + 0.19, 400, 200, 0.15);
}

/** Test seam and teardown. */
export function _resetForTests(): void {
  try { ctx?.close(); } catch { /* already closed */ }
  ctx = null;
}
